import "core-js/modules/es.map.get-or-insert.js";
import "core-js/modules/es.map.get-or-insert-computed.js";
import "core-js/modules/web.url-search-params.delete.js";
import "core-js/modules/web.url-search-params.has.js";
import "core-js/modules/web.url-search-params.size.js";
import { __decorate } from "tslib";
import { Component, Vue } from 'vue-property-decorator';
import Pagination from '@/components/Pagination/index.vue';
import { createDebitNoteForInterest, downloadOverdueInvoicesExcel, getOverdueInvoices, sendOverdueReminderMessage } from '@/api/ledgers';
import { getDealerById, searchDealers } from '@/api/dealers';
let default_1 = class extends Vue {
  constructor() {
    super(...arguments);
    this.tableKey = 0;
    this.total = 0;
    this.list = [];
    this.listLoading = true;
    this.dealerListLoading = false;
    this.dealerList = [];
    this.downloadLoading = false;
    this.listQuery = {
      dealerId: null,
      paginate: true,
      page: 1,
      limit: 10
    };
    // Settlement popup state
    this.settlementDialogVisible = false;
    this.activeRow = null;
    // Build the Intl formatter once instead of on every cell render —
    // el-table re-renders cells frequently (sort/resize/page change) and
    // `new Intl.NumberFormat()` is comparatively expensive to construct.
    this.currencyFormatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    });
    // Same logic for dates: many rows share identical invoiceDate/dueDate
    // values (same batch of invoices), so cache the formatted string by
    // the raw ISO value rather than re-parsing/re-formatting per cell.
    this.dateCache = new Map();
  }
  activated() {
    this.getList();
  }
  // Getter used by the template's v-if. Computed because Vue will only
  // re-evaluate it on dependency changes; here it simply reflects the
  // current date, which is enough for a once-a-day visibility check.
  get isSendWhatsappVisible() {
    return new Date().getDate() === 20;
  }
  get createDebitNoteVisible() {
    const now = new Date();
    const isFirstOfMonth = now.getDate() === 1;
    // One-off exception: also show on 3 Aug 2026.
    const isOneOffException = now.getFullYear() === 2026 && now.getMonth() === 7 && now.getDate() === 3;
    return isFirstOfMonth || isOneOffException;
  }
  async getList() {
    this.listLoading = true;
    try {
      const data = await getOverdueInvoices(this.listQuery);
      // Object.freeze tells Vue's reactivity system not to bother making
      // each row (and its nested settledBy array) reactive, since this is
      // read-only display data. Skips defineReactive walks across
      // potentially hundreds of rows/cells on every page change.
      this.list = Object.freeze(data.data);
      this.total = data.total;
    } catch (error) {
      console.error('Error fetching overdue invoices:', error);
    } finally {
      this.listLoading = false;
    }
  }
  formatCurrency(value) {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(num)) return '—';
    return this.currencyFormatter.format(num);
  }
  formatDate(value) {
    if (!value) return '—';
    const cached = this.dateCache.get(value);
    if (cached) return cached;
    const formatted = new Date(value).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    this.dateCache.set(value, formatted);
    return formatted;
  }
  openSettlementDialog(row) {
    this.activeRow = row;
    this.settlementDialogVisible = true;
  }
  async getDealerById(dealerId = null) {
    this.dealerListLoading = true;
    const data = await getDealerById(dealerId);
    if (data) {
      this.dealerList = [data];
    }
    this.dealerListLoading = false;
  }
  async getDealerList(query = null) {
    try {
      const isNumeric = /^[0-9]+$/.test(query);
      if (!query || (isNumeric ? query.length < 1 : query.length < 2)) {
        this.dealerList = [];
        return;
      }
      const searchParams = {
        keyword: query,
        searchBy: isNumeric ? 'dealerId' : 'name',
        applyBranchIdFilter: true,
        fromDealer: true
      };
      this.dealerList = await searchDealers(searchParams);
    } catch (error) {} // eslint-disable-line  no-empty
  }
  async handleFilter() {
    await this.getList();
  }
  async handleExcelDownload() {
    this.downloadLoading = false;
    try {
      this.listQuery.paginate = false; // Get all data without pagination for Excel export
      const query = this.listQuery;
      const blob = await downloadOverdueInvoicesExcel(query);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'overdue-invoices.xlsx');
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading Excel:', error);
      this.$message.error('Error downloading Excel');
    } finally {
      this.downloadLoading = false;
    }
  }
  // Uses Element UI's $confirm (already implicitly available since the
  // component uses this.$message elsewhere). OK -> resolves the promise,
  // Cancel/close -> rejects it, which we just swallow silently.
  async handleSendWhatsapp() {
    try {
      await this.$confirm('Are you sure?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      });
    } catch {
      // Cancelled — do nothing
      return;
    }
    try {
      await sendOverdueReminderMessage();
      this.$message.success('Message sent');
    } catch (error) {
      console.error('Error sending Whatsapp reminder:', error);
      this.$message.error('Error sending message');
    }
  }
  async handleCreateDebitNote() {
    try {
      await this.$confirm('Are you sure?', 'Confirm', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      });
    } catch {
      // Cancelled — do nothing
      return;
    }
    try {
      await createDebitNoteForInterest();
      this.$message.success('Debit Note Created');
    } catch (error) {
      console.error('Error Creating Debit Note:', error);
      this.$message.error('Error creating debit note');
    }
  }
};
default_1 = __decorate([Component({
  name: 'OverDueInvoicesList',
  components: {
    Pagination
  }
})], default_1);
export default default_1;